import { Alert, Box, MenuItem, Stack, SvgIconProps, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { BenefitDTOBenefitEnum, CustomBenefitDTO } from 'probonio-shared-ui/api';
import { CUSTOM_BENEFIT_ICONS } from 'probonio-shared-ui/component/icon';
import { apis, downloadResponse, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useTenantID } from 'probonio-shared-ui/module/me';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../../component/dialog';
import { DialogLoadingButton } from '../../../component/dialog/DialogLoadingButton';
import { DatePickerControl } from '../../../component/form/DatePickerControl';
import { SelectControl } from '../../../component/form/SelectControl';
import { getBenefitIcon } from '../BenefitIcon';

const CUSTOM_PREFIX = 'CUSTOM:';

interface FormFields {
  startMonth: Date;
  endMonth: Date;
  benefits: string[];
}

interface Props {
  dialogState: DialogState;
}

const MIN_DATE = DateTime.now().minus({ months: 12 }).startOf('month').toJSDate();
const MAX_DATE = DateTime.now().plus({ months: 1 }).startOf('month').toJSDate();

interface BenefitOption {
  value: string;
  label: string;
  icon?: React.ComponentType<SvgIconProps>;
}

export const EmployeeExportDialog: React.FC<Props> = ({ dialogState }) => {
  const { t } = useTranslation('usersModule');
  const theme = useTheme();
  const getTenantId = useTenantID();
  const [isDownloading, setIsDownloading] = useState(false);

  const { control } = useForm<FormFields>({
    defaultValues: {
      startMonth: DateTime.now().startOf('month').toJSDate(),
      endMonth: DateTime.now().startOf('month').toJSDate(),
      benefits: [],
    },
  });

  const defaultMonth = DateTime.now().startOf('month').toJSDate();
  const startMonth = useWatch({ control, name: 'startMonth', defaultValue: defaultMonth });
  const endMonth = useWatch({ control, name: 'endMonth', defaultValue: defaultMonth });
  const selectedBenefits = useWatch({ control, name: 'benefits', defaultValue: [] });

  const startMonthStr = DateTime.fromJSDate(startMonth).toFormat('yyyy-MM');
  const endMonthStr = DateTime.fromJSDate(endMonth).toFormat('yyyy-MM');

  const { data: customBenefits } = useTenantQuery(
    ['benefits', 'custom', apis.customBenefits.listCustomBenefits],
    tenantId => apis.customBenefits.listCustomBenefits({ tenantId }).then(res => res.data),
    { enabled: dialogState.isOpen, staleTime: 60_000 },
  );

  const { data: usedBenefits, isFetching: isFetchingUsedBenefits } = useTenantQuery(
    [apis.benefits.getUsedBenefits.name, { startMonth: startMonthStr, endMonth: endMonthStr }],
    tenantId => apis.benefits.getUsedBenefits({ tenantId, startMonth: startMonthStr, endMonth: endMonthStr }).then(res => res.data),
    { enabled: dialogState.isOpen, staleTime: 60_000 },
  );

  const benefitOptions = useMemo<BenefitOption[]>(() => {
    if (!usedBenefits) {
      return [];
    }
    const usedCustomIdSet = new Set(usedBenefits.customBenefitIds);
    const standardOptions: BenefitOption[] = usedBenefits.benefits
      .filter(({ benefit }) => benefit !== BenefitDTOBenefitEnum.Custom)
      .map(({ benefit, label }) => ({
        value: benefit,
        label,
        icon: getBenefitIcon(benefit as BenefitDTOBenefitEnum),
      }));
    const customOptions: BenefitOption[] = (customBenefits?.results ?? [])
      .filter(custom => usedCustomIdSet.has(custom.id))
      .map((custom: CustomBenefitDTO) => ({
        value: `${CUSTOM_PREFIX}${custom.id}`,
        label: custom.title,
        icon: CUSTOM_BENEFIT_ICONS[custom.iconName],
      }));
    return [...standardOptions, ...customOptions];
  }, [customBenefits, usedBenefits]);

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    benefitOptions.forEach(option => map.set(option.value, option.label));
    return map;
  }, [benefitOptions]);

  const benefitMenuItems = useMemo(
    () =>
      benefitOptions.map(option => {
        const Icon = option.icon;
        return (
          <MenuItem key={option.value} value={option.value}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflow: 'hidden',
                width: '100%',
                alignItems: 'center',
              }}
            >
              {Icon && <Icon color="primary" />}
              {option.label}
            </Box>
          </MenuItem>
        );
      }),
    [benefitOptions],
  );

  const renderBenefits = useCallback(
    (selected: unknown) => {
      const values = selected as string[];
      if (values.length === 0) {
        return <em>{t('usersList.exportDialog.benefitsAll')}</em>;
      }
      return values.map(value => labelByValue.get(value) ?? value).join(', ');
    },
    [labelByValue, t],
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const benefits: BenefitDTOBenefitEnum[] = [];
      const customBenefitIds: string[] = [];
      for (const value of selectedBenefits) {
        if (value.startsWith(CUSTOM_PREFIX)) {
          customBenefitIds.push(value.slice(CUSTOM_PREFIX.length));
        } else {
          benefits.push(value as BenefitDTOBenefitEnum);
        }
      }
      const response = await apis.benefits.exportBenefits({
        tenantId: getTenantId(),
        startMonth: startMonthStr,
        endMonth: endMonthStr,
        benefits: benefits.length > 0 ? benefits : undefined,
        customBenefitIds: customBenefitIds.length > 0 ? customBenefitIds : undefined,
      });
      downloadResponse(response, 'Used_Benefits.csv');
      dialogState.handleClose();
    } finally {
      setIsDownloading(false);
    }
  }, [getTenantId, startMonthStr, endMonthStr, selectedBenefits, dialogState]);

  const noUsedBenefits =
    !isFetchingUsedBenefits && !!usedBenefits && usedBenefits.benefits.length === 0 && usedBenefits.customBenefitIds.length === 0;
  const benefitSelectDisabled = isFetchingUsedBenefits || benefitOptions.length === 0;
  const downloadDisabled = isDownloading || isFetchingUsedBenefits || noUsedBenefits;

  return (
    <BasicDialog
      dialogState={dialogState}
      fullWidth
      maxWidth="xs"
      title={t('usersList.exportDialog.title')}
      content={
        <Stack
          spacing={2}
          sx={{
            mt: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.secondary.main,
              py: 1,
              px: 2,
              textAlign: 'center',
              borderRadius: '8px',
            }}
          >
            <Typography color={theme.palette.text.secondary}>{t('usersList.exportDialog.message')}</Typography>
          </Box>
          <Box>
            <Typography
              gutterBottom
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('usersList.exportDialog.startMonth')}
            </Typography>
            <DatePickerControl
              monthYearPicker
              fullWidth
              control={control}
              name="startMonth"
              label=""
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
            />
          </Box>
          <Box>
            <Typography
              gutterBottom
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('usersList.exportDialog.endMonth')}
            </Typography>
            <DatePickerControl
              monthYearPicker
              fullWidth
              control={control}
              name="endMonth"
              label=""
              minDate={startMonth ?? MIN_DATE}
              maxDate={MAX_DATE}
            />
          </Box>
          <Box sx={{ '& label': { display: 'none' } }}>
            <Typography
              gutterBottom
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('usersList.exportDialog.benefits')}
            </Typography>
            <SelectControl
              control={control}
              multiple
              name="benefits"
              fullWidth
              displayEmpty
              disabled={benefitSelectDisabled}
              renderValue={renderBenefits}
            >
              {benefitMenuItems}
            </SelectControl>
          </Box>
          {noUsedBenefits && <Alert severity="warning">{t('usersList.exportDialog.noUsedBenefits')}</Alert>}
        </Stack>
      }
      actions={
        <DialogLoadingButton
          sx={{ mt: 1 }}
          variant="text"
          color="primary"
          disabled={downloadDisabled}
          loading={isDownloading}
          onClick={handleDownload}
        >
          {t('usersList.exportDialog.download')}
        </DialogLoadingButton>
      }
    />
  );
};
